import { useCallback } from 'react'
import { graphql, useMutation } from 'react-relay'

import {
  type useSparkMutation as UseSparkMutation,
  type useSparkMutation$data
} from '@/__generated__/useSparkMutation.graphql'

type CommitAsyncFn = (toUserId: string) => Promise<useSparkMutation$data>

export const useSparkMutation = (): [CommitAsyncFn, boolean] => {
  const [commit, isPending] = useMutation<UseSparkMutation>(graphql`
    mutation useSparkMutation($input: SendSparkInput!) {
      sendSpark(input: $input) {
        query {
          currentUser {
            sparkBalance
          }
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (toUserId: string) =>
      new Promise((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            // errors?.length, not truthiness: Relay can hand back an empty [] which would
            // otherwise fall through and reject a successful send with "Unable to send Spark".
            if (errors?.length) {
              // A graphql-ws replay of an in-flight send re-runs the mutation after the first
              // committed; send_spark's guard 3 then raises this for a Spark that WAS delivered and
              // charged exactly once. Treat it as delivered so callers advance instead of
              // error-toasting a successful Spark — but only when it is the SOLE error, so a mixed
              // response carrying a real failure still rejects. (No spark_sent track: already sent.)
              if (errors.every((error) => error?.message === 'You already sparked this person')) {
                resolve(response)
                return
              }

              // Relay passes PayloadError plain objects (not Error instances); wrap so callers'
              // `error instanceof Error ? error.message` surfaces the send_spark guard copy instead of
              // "[object Object]".
              reject(new Error(errors[0]?.message ?? 'Unable to send Spark'))
            } else {
              resolve(response)
            }
          },
          // transport-level failures (offline, 5xx, WS drop) invoke onError, not onCompleted — without
          // this the promise would never settle and the confirm dialog would hang in its loading state.
          onError: (error) => reject(error),
          variables: { input: { toUserId } }
        })
      ),
    [commit]
  )

  return [commitAsync, isPending]
}
